import { useState } from "react";
import { useMutation } from "@apollo/client/react";
import {
    Body,
    Button,
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    Text,
} from "@athena/ui";
import { Gift } from "lucide-react";
import * as Sentry from "@sentry/react";
import { toast } from "sonner";
import { SendGiftCardDocument } from "@/graphql/chat.generated";
import type { SendGiftCardMutation } from "@/graphql/chat.generated";

/**
 * Sends a gift card from the coach's composer (roadmap 9.2, ATL-165).
 *
 * The amounts mirror manual-rw's menu, but the flow differs in two ways that matter.
 *
 * manual-rw generates a link, shows it, and leaves the coach to copy it into the message
 * themselves — so a generated card can be paid for and then never sent. Here one action orders it
 * and posts it, and the mutation does both.
 *
 * And it confirms first. This spends real money and cannot be undone: a claimed card is gone.
 * `.claude/rules/ui.md` wants the blast radius on screen before the action is accepted, with the
 * confirm button repeating the action rather than saying OK.
 */

/** Matches GIFT_CARD_DENOMINATIONS server-side, which is what actually enforces it. */
const AMOUNTS = [
    5,
    10,
    15,
    20,
    25,
] as const;

interface GiftCardButtonProps {
    chatId: string;
    /** Named in the confirmation so the coach is agreeing to a person, not just an amount. */
    recipientName: string;
    disabled?: boolean;
    onSent: (message: NonNullable<SendGiftCardMutation["sendGiftCard"]>["message"]) => void;
}

export function GiftCardButton({ chatId, recipientName, disabled, onSent }: GiftCardButtonProps) {
    const [
        amount,
        setAmount,
    ] = useState<number | null>(null);
    /**
     * Minted once per confirmation, not per click. Reused if the coach retries after a failure,
     * which is the case where we cannot tell whether the first attempt charged us — Tremendous
     * dedupes on it, so the retry cannot buy a second card.
     */
    const [
        idempotencyKey,
        setIdempotencyKey,
    ] = useState<string | null>(null);

    const [
        sendGiftCard,
        { loading },
    ] = useMutation(SendGiftCardDocument);

    const open = (value: number) => {
        setAmount(value);
        setIdempotencyKey(crypto.randomUUID());
    };

    const confirm = async () => {
        if (amount === null || idempotencyKey === null) return;
        try {
            const { data } = await sendGiftCard({
                variables: { input: { chatId, amount, idempotencyKey } },
            });
            const sent = data?.sendGiftCard;
            if (sent) onSent(sent.message);
            setAmount(null);
            setIdempotencyKey(null);
            toast.success(`$${amount} gift card sent to ${recipientName}.`);
        } catch (error) {
            Sentry.captureException(error);
            // The server's message names the actual reason — an empty balance, a bad campaign —
            // and the dialog stays open so the same key can be retried without buying twice.
            toast.error((error as Error).message || "The gift card could not be sent.");
        }
    };

    return (
        <>
            <div className="flex items-center gap-1">
                <Gift className="text-muted-foreground h-3.5 w-3.5" aria-hidden="true" />
                {AMOUNTS.map((value) => (
                    <Button
                        key={value}
                        type="button"
                        size="sm"
                        variant="ghost"
                        className="w-auto px-2"
                        disabled={disabled || loading}
                        aria-label={`Send a $${value} gift card`}
                        onClick={() => open(value)}
                    >
                        ${value}
                    </Button>
                ))}
            </div>

            <Dialog open={amount !== null} onOpenChange={(next) => !next && !loading && setAmount(null)}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>
                            Send {recipientName} a ${amount} gift card?
                        </DialogTitle>
                        <DialogDescription>
                            This spends ${amount} from the Tremendous balance now, and a claimed card cannot be taken
                            back.
                        </DialogDescription>
                    </DialogHeader>

                    <Body>The claim link is posted into this conversation as a message from you.</Body>

                    <DialogFooter>
                        <Button variant="outline" className="w-auto" disabled={loading} onClick={() => setAmount(null)}>
                            Cancel
                        </Button>
                        {/* Repeats the action and its cost, rather than "OK". */}
                        <Button className="w-auto" disabled={loading} onClick={confirm}>
                            {loading ? "Sending…" : `Send $${amount}`}
                        </Button>
                    </DialogFooter>
                    <Text muted className="text-xs">
                        Sent cards are recorded against this person for reporting.
                    </Text>
                </DialogContent>
            </Dialog>
        </>
    );
}
